import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from "@utahdts/utah-design-system";
import {
  GET_SITE_VISIT_OPTION,
  SAVE_SITE_VISIT_OPTION,
} from "../../queries/siteVisitQrys/siteVisitOptionsQrys";
import { formatDate } from "../../utilities/commonFunctions/formattingFunctions";
import "./siteVisits.css";

const STATUS_OPTIONS = ["active", "inactive"];
const EMPTY_ENTRY = () => ({ code: "", description: "", status: "active", isExisting: false });

const OptionEntryModal = ({
  onClose,
  isNewOption,
  selectedOptionPk,
  canEdit,
  refetch,
}) => {
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formStatus, setFormStatus] = useState("active");
  const [formEntries, setFormEntries] = useState([]);
  const [formMeta, setFormMeta] = useState({ createdUser: "", createdTs: "", lastUpdatedUser: "", lastUpdatedTs: "" });
  const [saveError, setSaveError] = useState(null);

  const [fetchOption, { data: optionData, loading: optionLoading, error: optionError }] = useLazyQuery(GET_SITE_VISIT_OPTION, { fetchPolicy: "network-only" });
  const [saveOption, { loading: saving }] = useMutation(SAVE_SITE_VISIT_OPTION);

  useEffect(() => {
    setSaveError(null);

    if (isNewOption) {
      setFormDisplayName("");
      setFormStatus("active");
      setFormMeta({ createdUser: "", createdTs: "", lastUpdatedUser: "", lastUpdatedTs: "" });
      setFormEntries(Array.from({ length: 5 }, EMPTY_ENTRY));
      return;
    }

    if (selectedOptionPk == null) {
      setFormEntries([EMPTY_ENTRY()]);
      return;
    }

    setFormEntries([]);
    fetchOption({ variables: { optionId: selectedOptionPk } });
  }, [isNewOption, selectedOptionPk, fetchOption]);

  useEffect(() => {
    if (!optionData?.getSiteVisitOption) return;
    const opt = optionData.getSiteVisitOption;
    setFormDisplayName(opt.displayName ?? "");
    setFormStatus(opt.status ?? "active");
    setFormMeta({
      createdUser: opt.createdUser ?? "",
      createdTs: opt.createdTs ?? "",
      lastUpdatedUser: opt.lastUpdatedUser ?? "",
      lastUpdatedTs: opt.lastUpdatedTs ?? "",
    });
    setFormEntries([
      ...(opt.entries ?? []).map((e) => ({ ...e, isExisting: true })),
      EMPTY_ENTRY(),
    ]);
  }, [optionData]);

  useEffect(() => {
    if (!optionLoading && !isNewOption && optionError && formEntries.length === 0) {
      setFormEntries([EMPTY_ENTRY()]);
    }
  }, [optionLoading, isNewOption, optionError, formEntries.length]);

  const handleEntryChange = (index, field, value) => {
    setFormEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
  };

  const handleAddRow = () => setFormEntries((prev) => [...prev, EMPTY_ENTRY()]);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveOption({
        variables: {
          input: {
            optionId: selectedOptionPk,
            displayName: formDisplayName,
            status: formStatus,
            entries: formEntries
              .filter((e) => e.code.trim() !== "")
              .map(({ code, description, status }) => ({ code, description, status })),
          },
        },
      });
      await refetch();
      onClose();
    } catch (err) {
      setSaveError(err.message);
    }
  };

  return (
    <Modal className="modal--large" onClose={onClose} onEscape={onClose}>
      <ModalTitle>Option</ModalTitle>
      <ModalContent>
        <div className="site-visits-option-fields-grid">
          <div>
            <label htmlFor="opt-display-name"><strong>Display Name:</strong></label>
            <input
              id="opt-display-name"
              type="text"
              value={formDisplayName}
              onChange={(e) => setFormDisplayName(e.target.value)}
              maxLength={255}
              readOnly={!canEdit}
              className="site-visits-field-control"
            />
          </div>
          <div>
            <label htmlFor="opt-status"><strong>Status:</strong></label>
            <select
              id="opt-status"
              value={formStatus}
              onChange={(e) => setFormStatus(e.target.value)}
              disabled={!canEdit}
              className="site-visits-field-control"
            >
              <option value="">------</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          {!isNewOption && (
            <>
              <div>
                <strong>Last Updated:</strong>
                <span className="site-visits-meta-value">
                  {formMeta.lastUpdatedTs
                    ? `${formatDate(formMeta.lastUpdatedTs)} by ${formMeta.lastUpdatedUser}`
                    : "—"}
                </span>
              </div>
              <div>
                <strong>Created:</strong>
                <span className="site-visits-meta-value">
                  {formMeta.createdTs
                    ? `${formatDate(formMeta.createdTs)} by ${formMeta.createdUser}`
                    : "—"}
                </span>
              </div>
            </>
          )}
        </div>

        {optionLoading ? (
          <p>Loading entries...</p>
        ) : (
          <>
            <table className="site-visits-entries-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {formEntries.map((entry, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={entry.code}
                        readOnly={entry.isExisting || !canEdit}
                        onChange={(e) => handleEntryChange(index, "code", e.target.value.toUpperCase())}
                        maxLength={255}
                        placeholder={entry.isExisting ? "" : "A-Z, 0-9, _"}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={entry.description}
                        readOnly={!canEdit}
                        onChange={(e) => handleEntryChange(index, "description", e.target.value)}
                        maxLength={255}
                      />
                    </td>
                    <td>
                      <select
                        value={entry.status}
                        disabled={!canEdit}
                        onChange={(e) => handleEntryChange(index, "status", e.target.value)}
                      >
                        <option value="">------</option>
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {optionError && !isNewOption && (
              <p className="site-visits-entries-error">Entry data unavailable - add rows manually.</p>
            )}
          </>
        )}

        {canEdit && (
          <Button variant="secondary" className="mt-spacing" onClick={handleAddRow}>
            + Add Row
          </Button>
        )}

        {saveError && (
          <p className="site-visits-save-error">{saveError}</p>
        )}
      </ModalContent>
      <ModalFooter>
        <div className="site-visits-modal-footer-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {canEdit && (
            <Button
              appearance="solid"
              variant="primary"
              onClick={handleSave}
              isDisabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OptionEntryModal;
